import Close from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router";
import { useToken } from "../../server/auth/use-token";
import { fromBase64 } from "../../utils/base64";
import { downloadFiles } from "../../utils/download-files";
import { useAnalytics } from "../analytics/analytics";
import { CeTextField } from "../generic/components/form-fields/ce-text-field";
import { useAccountId } from "../generic/hooks/use-account-id";
import { useGetDesignHuddleAccessToken } from "./use-design-huddle-access-token";
import { getDesignHuddleRenderUrls, getProgressText } from "./utils";

import type { DialogController } from "../../utils/hooks/use-dialog";
import type { FC } from "react";

export type ExportHandler = (options: {
  downloadUrls: string[];
  filename: string;
  format: string;
}) => void;

export const DesignHuddleExportModal: FC<{
  projectId: string;
  filename: string;
  setFilename: (f: string) => void;
  dialog: DialogController<unknown>;
  onExport: ExportHandler;
  autostart?: boolean;
  saveHandler: () => void;
  multipage: boolean;
  format?: string;
}> = ({
  projectId,
  dialog,
  filename,
  setFilename,
  onExport,
  autostart,
  saveHandler,
  multipage,
  format: initialFormat = multipage ? "pdf" : "png",
}) => {
  const getToken = useToken();
  const getAccessToken = useGetDesignHuddleAccessToken();
  const { gaEvent } = useAnalytics();

  const accountId = useAccountId();

  const [format, setFormat] = useState(initialFormat);
  const [renderState, setRenderState] = useState<{
    loading: boolean;
    urls?: string[];
    error?: string;
    progress: number;
  }>({ loading: false, progress: 0 });

  const [searchParams] = useSearchParams();
  const { contentType } = useMemo(() => {
    const d = searchParams.get("d");
    if (!d) return { contentType: "" };

    const decoded = fromBase64(d || "");
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return JSON.parse(decoded) as { contentType: string };
  }, [searchParams]);

  const handleExport = useCallback(() => {
    void (async () => {
      if (!accountId) return;
      if (renderState.loading) return;
      setRenderState({ loading: true, progress: 0 });

      try {
        saveHandler(); // This is fire-and-forget
        const downloadUrls = await getDesignHuddleRenderUrls({
          getToken,
          accessToken: await getAccessToken([projectId]),
          projectId,
          filename,
          format,
          onProgress: (progress) => {
            if (progress !== null) {
              setRenderState((prev) => ({ ...prev, progress }));
            }
          },
        });
        setRenderState({ loading: false, urls: downloadUrls, progress: 100 });
        onExport({ downloadUrls, filename, format });
      } catch (error) {
        if (error instanceof TypeError) {
          setRenderState({ loading: false, error: error.message, progress: 0 });
        }
      }
    })();
  }, [
    accountId,
    renderState.loading,
    getToken,
    getAccessToken,
    projectId,
    filename,
    format,
    onExport,
    saveHandler,
  ]);

  useEffect(() => {
    if (renderState.urls) {
      void downloadFiles(renderState.urls, filename, format);
    }
  }, [filename, format, renderState.urls]);

  const handleClose = useCallback(() => {
    dialog.handleClose();
    setRenderState({ loading: false, progress: 0 });
  }, [dialog]);

  useEffect(() => {
    if (autostart) {
      handleExport();
    }
  }, [autostart, handleExport]);

  if (renderState.error) {
    toast.error(renderState.error);
  }

  const formatSelectComponent = useMemo(() => {
    if (contentType === "Video") {
      // oxlint-disable-next-line react/set-state-in-render -- setState inside useMemo; needs a real fix, tracked separately
      setFormat("mp4");
      return <Typography>Your design will be exported as an MP4</Typography>;
    }

    return (
      <Autocomplete
        value={format}
        options={["png", "pdf"]}
        onChange={(_, newValue) => setFormat(newValue!)}
        renderInput={(params) => (
          <CeTextField analyticsCategory="design_huddle" {...params} label="Format" />
        )}
      />
    );
  }, [contentType, format, setFormat]);

  return (
    <Dialog open={dialog.open} onClose={handleClose} fullWidth>
      <DialogTitle display="flex" component="div">
        <Typography flexGrow={1} variant="h5">
          {renderState.urls
            ? "Export Successful"
            : renderState.loading
              ? "Rendering Design for Export"
              : "Export Your Design"}
        </Typography>
        <IconButton data-analytics-id="design-huddle-export-dialog-close" onClick={handleClose}>
          <Close data-testid="CloseIcon" />
        </IconButton>
      </DialogTitle>

      {renderState.urls ? (
        <Success urls={renderState.urls} title={filename} format={format} onClose={handleClose} />
      ) : renderState.loading ? (
        <Progress onClose={handleClose} progress={renderState.progress} />
      ) : (
        <DialogContent>
          <Stack gap={4} mt={2}>
            <CeTextField
              analyticsCategory="design_huddle"
              label="Export as"
              value={filename}
              onChange={(e) => setFilename(e.currentTarget.value)}
            />
            {formatSelectComponent}
            <DialogActions>
              <Button
                data-analytics-id="design-huddle-export-dialog-export"
                onClick={() => {
                  gaEvent("multiformat_button", {
                    action: "click",
                    value: "Export Image in Export Modal",
                  });
                  handleExport();
                }}
                loading={renderState.loading}
                variant="contained"
              >
                Export
              </Button>
            </DialogActions>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  );
};

export const Progress: FC<{
  onClose: () => void;
  progress?: number;
}> = ({ onClose, progress }) => (
  <DialogContent>
    <Stack gap={4}>
      <Typography>
        {progress && progress >= 25 ? getProgressText(progress) : "Exporting..."}
      </Typography>
      <LinearProgress
        variant={progress !== undefined ? "determinate" : "indeterminate"}
        value={progress}
      />
      <DialogActions>
        <Button
          data-analytics-id="design-huddle-export-dialog-cancel"
          variant="outlined"
          color="warning"
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Stack>
  </DialogContent>
);

export const Success: FC<{
  urls: string[];
  title: string;
  format: string;
  onClose: () => void;
}> = ({ urls, title, format, onClose }) => {
  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    downloadFiles(urls, title, format).catch((error) => {
      console.error("Download failed:", error);
    });
  };

  return (
    <DialogContent>
      <Stack gap={4}>
        <Typography>Download should start automatically</Typography>
        <Typography variant="body2">
          If your download does not start automatically,{" "}
          <Link onClick={handleOnClick} component="a" target="_blank" sx={{ cursor: "pointer" }}>
            click here
          </Link>
        </Typography>
        <DialogActions>
          <Button
            data-analytics-id="design-huddle-export-dialog-ok"
            onClick={onClose}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Stack>
    </DialogContent>
  );
};
